import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"
import PolygonObject from "@/signomatic/app/objects/polygon"
import pathRounding from "@/signomatic/util/path_rounding"
import PolygonPath from "@/signomatic/util/polygon_path"

export default abstract class Polygon extends Form {
  protected path(width: number, height: number, rounding?: number, _curves?: any, forClip?: boolean) {
    const points = this.points(width, height)

    if (!forClip && this.drawables.hasBorder) {
      return new PolygonObject(points, this.defaultParams)
    }

    let path = PolygonPath(points)

    if (rounding) {
      path = pathRounding(path, rounding).join("")
    }

    return new PathObject(path, this.defaultParams)
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] | null {
    if (!["stop", "arrow_left", "arrow_right"].includes(this.state.shape)) {
      return null
    }

    const [side, rest] = this.snappableCommands(width, height)

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  private snappableCommands(width: number, height: number) {
    const points = this.points(width, height)
    const [sideStart, sideEnd] = this.snappableSidePoints()

    const side = [this.pathMoveTo(points[sideStart].x, points[sideStart].y)]
    side.push(this.pathLineToAbs(points[sideEnd].x, points[sideEnd].y))

    const reststart = points[sideEnd]
    const restpoints: Point[] = []

    for (let index = sideEnd; index < points.length; index++) {
      restpoints.push(points[index])
    }

    for (let index = 0; index < sideEnd; index++) {
      restpoints.push(points[index])
    }

    const rest = [this.pathMoveTo(reststart.x, reststart.y)]

    for (const point of restpoints) {
      rest.push(this.pathLineToAbs(point.x, point.y))
    }

    return [side, rest]
  }

  private snappableSidePoints() {
    switch (this.state.shape) {
      case "stop":
        return [3, 4]
      case "arrow_left":
      case "arrow_right":
        return [0, 1]
      default:
        return [0, 1]
    }
  }

  protected cutlinePath(width: number, height: number, rounding?: number) {
    let path = PolygonPath(this.points(width, height))

    if (rounding) {
      path = pathRounding(path, rounding).join("")
    }

    return new PathObject(path, this.defaultParams)
  }

  protected abstract points(width: number, height: number): Point[]

  protected cutlineGappedPath(width: number, height: number): DrawableMixin | undefined {
    if (this.state.shape === "stop" && (this.isStainlessSteelSign() || this.isEtchedAluminiumSign())) {
      const styles = { stroke: "#f08894", strokeWidth: this.drawables.cutlineWidth, fill: "transparent" }
      return new PathObject(this.polygonCutLine(width, height).join(""), {
        ...this.defaultParams,
        ...styles,
      })
    }

    return
  }

  protected polygonCutLine(width: number, height: number): string[] {
    const gap = this.gapLength()
    const legLength = Calc.mmToPx(2.5)

    const points = this.points(width, height)

    const topMidX = 0
    const topMidY = -height / 2

    const rightMidX = width / 2
    const rightMidY = 0

    const bottomMidX = 0

    const leftMidX = -width / 2
    const leftMidY = 0

    const segment1 = []
    segment1.push(this.pathMoveTo(topMidX, topMidY - legLength))
    segment1.push(this.pathLineV(legLength))
    segment1.push(this.pathLineH(points[6].x))
    segment1.push(this.pathLineToAbs(points[7].x, points[7].y))
    segment1.push(this.pathLineVAbs(rightMidY))

    const segment2 = []
    segment2.push(this.pathMoveTo(rightMidX + legLength, rightMidY + gap))
    segment2.push(this.pathLineH(-legLength))
    segment2.push(this.pathLineV(points[0].y))
    segment2.push(this.pathLineToAbs(points[1].x, points[1].y))
    segment2.push(this.pathLineHAbs(bottomMidX))
    segment2.push(this.pathLineH(points[2].x + gap))
    segment2.push(this.pathLineToAbs(points[3].x, points[3].y))
    segment2.push(this.pathLineVAbs(leftMidY))

    const segment3 = []
    segment3.push(this.pathMoveTo(leftMidX - legLength, leftMidY - gap))
    segment3.push(this.pathLineH(legLength))
    segment3.push(this.pathLineV(points[4].y + gap))
    segment3.push(this.pathLineToAbs(points[5].x, points[5].y))
    segment3.push(this.pathLineHAbs(topMidX - gap))

    return [segment1.join(" "), segment2.join(" "), segment3.join(" ")]
  }
}
